//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ReactNode, useContext } from 'react';
import { ConfigContext } from '@/contextAPI/configContext';
import styled from '@emotion/styled';
import { Card, CardHeader, LinearProgress, LinearProgressProps, Skeleton, emphasize, useTheme } from '@mui/material';
import { DeepPartial } from '@/redux/slices/slice_types';
import { AnimationSpec } from 'chart.js';
import { selectDashboardIsLoading, selectGraphsStatus } from '@/redux/selectors/dashboardSelectors';
import { useAppSelector } from '@/redux/hooks';
import BarChart, { BarChartData } from './Reusable/BarChart';
import DonutChart, { DonutChartData } from './Reusable/DonutChart';
import LineChart, { LineChartData } from './Reusable/LineChart';
import PieChart, { PieChartData } from './Reusable/PieChart';
import { ColorMap, generateChartColorMap } from './Reusable/chartConfig';

/** ***********************************
 *
 * INSTRUCTIONS ON HOW TO ADD A NEW CONFIGURATION TO THE DASHBOARD WIDGET
 *
 * 1. Add new type for this chart's data to DashboardWidget.tsx
 *    --> example: type NewData = { type: 'new', data: NewChartData }
 *
 * 2. Update WidgetType to include the new chart type
 *    --> example: export type WidgetType = DonutData['type'] ... | NewData['type'];
 *
 * 3. Update ChartDataTypes to include the new chart type
 *    --> example: type ChartDataTypes = DonutData | ... | NewData;
 *
 * 4. Update DashboardWidgetProps to include the new chart type
 *    --> example: export type DashboardWidgetProps = WidgetData & (DonutData | ... | NewData);
 *
 * 5. Enable DashboardWidget to actually render the new chart type when the proper configs are passed in
 *    --> example: ... else if (chart.type === 'new') return <NewChart title={title} data={chart.data} loading={isLoading} />;
 *
 ************************************ */

export type WidgetData = {
  title: ReactNode;
  caption: string;
  linearProgress?: LinearProgressProps;
};

type DonutData = {
  type: 'donut';
  data: DonutChartData;
};

type LineData = {
  type: 'line';
  data: LineChartData;
};

type BarData = {
  type: 'bar';
  data: BarChartData;
};

type PieData = {
  type: 'pie';
  data: PieChartData;
};

type ChildrenData = {
  children: any;
};

export type BasicChartProps = {
  title: ReactNode;
  colors: ColorMap;
  animation: DeepPartial<AnimationSpec<any>>;
  loading: boolean | null;
};

export type WidgetType = DonutData['type'] | LineData['type'] | BarData['type'] | PieData['type'];
type ChartDataTypes = DonutData | LineData | BarData | PieData;

export type DashboardWidgetProps = WidgetData & (DonutData | LineData | BarData | PieData | ChildrenData) & { className?: string; children?: any; };

const DashboardWidget = ({ title, caption, linearProgress, className, ...rest }: DashboardWidgetProps) => {
  const { editable } = useAppSelector(selectGraphsStatus);
  const isLoading = useAppSelector(selectDashboardIsLoading);
  const colorMap = generateChartColorMap(useTheme().palette);
  const configContext = useContext(ConfigContext);
  const chartAnimation = configContext?.dashboardConfig?.chartAnimation;

  // Modify caption to add a period at the end if it's missing
  const updatedCaption = caption && !caption.endsWith('.') ? caption + '.' : caption;

  const renderChart = () => {
    if (rest?.children) {
      return (rest as ChildrenData).children(colorMap, isLoading, chartAnimation);
    }

    const chart: ChartDataTypes = rest as ChartDataTypes;
    if (chart.type === 'line') return <LineChart title={title} data={chart.data} colors={colorMap} loading={isLoading} animation={chartAnimation} />;
    else if (chart.type === 'donut')
      return <DonutChart title={title} data={chart.data} colors={colorMap} loading={isLoading} animation={chartAnimation} />;
    else if (chart.type === 'bar')
      return <BarChart title={title} data={chart.data} colors={colorMap} loading={isLoading} animation={chartAnimation} />;
    else if (chart.type === 'pie')
      return <PieChart title={title} data={chart.data} colors={colorMap} loading={isLoading} animation={chartAnimation} />;
  };

  return !editable ? (
    <CardWrapper className={className} loading={`${isLoading}`}>
      {linearProgress && !isLoading ? <LinearProgress {...linearProgress} /> : <LinearProgressPlaceholder />}
      {isLoading ? (
        <SkeletonWrapper>
          <Skeleton variant="text" width="50%" height={30} />
          <Skeleton variant="text" width="30%" height={30} />
        </SkeletonWrapper>
      ) : (
        <CardLabel title={title} subheader={updatedCaption} />
      )}
      <Chart>{renderChart()}</Chart>
    </CardWrapper>
  ) : (
    renderChart()
  );
};

export default DashboardWidget;

const CardWrapper = styled(Card)<{ loading: string }>`
  height: 100%;
  position: relative;
  opacity: ${({ loading }) => (loading === 'true' ? 0.4 : 1)};
  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `linear-gradient(212.15deg, ${emphasize(theme.palette.background.paper, 0.1)} 9.41%, ${theme.palette.background.paper} 38.94%)`
      : `linear-gradient(211.84deg, #FFFFFF 6.88%, rgba(255, 255, 255, 0.46) 83.4%)`};
  box-shadow: 0px 12px 26px 0px rgba(16, 30, 115, 0.06);
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ theme }) => theme.customOverrides.DashboardWidget}
`;

const Chart = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const CardLabel = styled(CardHeader)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};

  .MuiCardHeader-title {
    font-weight: 500;
  }

  .MuiCardHeader-subheader {
    font-weight: 400;
  }
`;

const SkeletonWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(2, 2, 0, 2)};
`;

const LinearProgressPlaceholder = styled.div`
  height: 4px;
  width: 100%;
  background: transparent;
`;
